import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toImportCodeUpper, toPartNumberUpper} from "./shared";

/**
 * @param {*} item
 * @returns {boolean}
 */
function isProductDimensionsSeedRow(item) {
    return (
        !!toPartNumberUpper(item?.partNumber) &&
        !!(item.length?.trim() || item.width?.trim() || item.height?.trim() || item.weight?.trim())
    );
}

/**
 * Product dimensions import — phased part + vendor resolution and per-row size payloads, then **grouped**
 * API requests `{ partId, sizes: [...] }[]` (not row-aligned; see `getBatchedApiRequestRecords` for this type).
 *
 * @param {Array<Object|null|undefined>} mappedData
 * @param {boolean} isFinalRun
 * @param {{ transformLengthValue: function(number, *): *, transformWeightValue: function(number, *): * }} transforms
 */
export class ProductDimensionsImportTransformer extends BaseImportTransformer {
    async run(mappedData, isFinalRun = false, transforms) {
        const {transformLengthValue, transformWeightValue} = transforms ?? {};
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, seedValid: boolean, partNoUpper: string, vendorCode: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            seedValid: isProductDimensionsSeedRow(item),
            partNoUpper: toPartNumberUpper(item?.partNumber),
            vendorCode: toImportCodeUpper(item?.vendorCode),
            codes: [],
        }));

        const uniqueParts = [
            ...new Set(rowState.filter((r) => r.seedValid && r.partNoUpper).map((r) => r.partNoUpper)),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        const uniqueVendorCodes = [...new Set(rowState.map((r) => r.vendorCode).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let vendorIds = {};
        if (uniqueVendorCodes.length > 0) {
            const vendorBundle = await this.ctx.fetchVendorInfoByVendorCodes(uniqueVendorCodes);
            vendorIds = vendorBundle.vendorIds;
        }

        const transformLength = (val) => {
            if (val === undefined || val === null || val === "") return undefined;
            const parsed = parseFloat(val.toString());
            if (Number.isNaN(parsed)) return undefined;
            return transformLengthValue(parsed, {toDefault: true, displayDecimals: 2});
        };

        const transformWeight = (val) => {
            if (val === undefined || val === null || val === "") return undefined;
            const parsed = parseFloat(val.toString());
            if (Number.isNaN(parsed)) return undefined;
            return transformWeightValue(parsed, {toDefault: true, displayDecimals: 2});
        };

        // ── Phase 1: part present but no dimension/weight column filled (CSV) ───────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !rs.seedValid) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        for (const rs of rowState) {
            // if (!rs.seedValid) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        for (const rs of rowState) {
            // if (!rs.seedValid) continue;
            if (rs.vendorCode && !vendorIds[rs.vendorCode]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_VENDOR);
            }
        }

        // ── Phase 4: seed row resolved but no usable numeric dimensions after parse ──
        for (const rs of rowState) {
            if (!rs.item || !rs.seedValid) continue;
            if (rs.codes.length) continue;
            const it = rs.item;
            const L = transformLength(it.length);
            const W = transformLength(it.width);
            const H = transformLength(it.height);
            const Wt = transformWeight(it.weight);
            if (L === undefined || W === undefined || H === undefined || Wt === undefined) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                invalidityCodes: [...new Set(rs.codes)],
                originalRow: rs.item,
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const individualRequests = rows.map((item, rowIndex) => {
            if (!item) return null;
            const rs = rowState[rowIndex];
            if (!rs.seedValid) {
                if (issueIndexSet.has(rowIndex)) {
                    return isFinalRun
                        ? null
                        : {
                            partId: null,
                            partNumber: item.partNumber,
                            vendorCode: item.vendorCode,
                            vendorPartNumber: item.vendorPartNumber,
                            length: item.length,
                            width: item.width,
                            height: item.height,
                            weight: item.weight,
                        };
                }
                return isFinalRun ? null : {partId: null, partNumber: item?.partNumber};
            }

            const partId = partIds[rs.partNoUpper];
            const vendorCode = rs.vendorCode || null;
            const vendorId = vendorCode ? vendorIds[rs.vendorCode] ?? null : null;

            if (issueIndexSet.has(rowIndex)) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        partNumber: item.partNumber,
                        vendorCode: item.vendorCode,
                        vendorPartNumber: item.vendorPartNumber,
                        length: item.length,
                        width: item.width,
                        height: item.height,
                        weight: item.weight,
                    };
            }

            const sizeEntry = {
                partId: parseInt(partId, 10),
                partNumber: rs.partNoUpper,
                vendorId,
                spireVendorId: vendorId,
                length: Number(transformLength(item.length)),
                width: Number(transformLength(item.width)),
                height: Number(transformLength(item.height)),
                weight: Number(transformWeight(item.weight)),
            };

            if (sizeEntry.length === undefined && sizeEntry.width === undefined && sizeEntry.height === undefined && sizeEntry.weight === undefined) {
                return null;
            }

            return sizeEntry;
        });

        const groupedByPart = {};
        individualRequests.forEach((req) => {
            if (!req || req.partId == null) return;
            if (!groupedByPart[req.partId]) {
                groupedByPart[req.partId] = {
                    partId: req.partId,
                    sizes: [],
                };
            }
            groupedByPart[req.partId].sizes.push(req);
        });

        const apiRequests = Object.values(groupedByPart);

        return {apiRequests, preventativeIssues};
    }
}
